import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { IntlProvider } from 'react-intl';
import { useAuth } from '../AuthContext';
import useAssuranceSettled from '../hooks/auth/useAssuranceSettled';
import { useCompanySettings } from '../CompanySettingsContext';
import { supabase } from '../supabase';
import { useAuthHandoffLocaleLock } from '../ui/splash/authHandoffHold';
import { applyDocumentNoTranslate } from '../utils/branding/brandNoTranslate';
import { runScreenCrossFade } from '../ui/theme/themeSwitchMotion';
import useLocaleResumeSync from './useLocaleResumeSync';

// Both locale catalogs are lazy — neither en.json nor es.json is bundled into
// main.js. Each lands in its own chunk (~330KB each, parsed only once cached).
//
// Contract:
//   • The active locale's catalog is loaded on first use; BootGate holds the
//     splash via `messagesReady` until it resolves (no raw-id flash).
//   • When locale = 'es', the EN catalog is ALSO kicked off in parallel so it
//     is ready as a fallback for any missing ES keys or a failed ES load.
//   • A failed catalog import retries once; if it fails again, BootGate unblocks
//     with whatever catalog is available (EN preferred) or empty messages as a
//     last resort — a frozen splash is always worse than English text.
//   • `localeReady` = auth + DB-pref resolved, and (no own language) the company
//     default read (3s bounded wait). `messagesReady` waits for the ACTIVE
//     catalog at boot, then never un-readies in the session (KAN-857: no EN flash
//     on lock screens; BootGate unmounts the app while blocking).
//   • See "Cold-start / bundle hygiene" in CLAUDE.md.

const catalogPromises = {};

function loadCatalog(code) {
  if (!catalogPromises[code]) {
    const p = (code === 'es'
      ? import('./messages/es.json')
      : import('./messages/en.json')
    ).then((m) => m.default || m);
    p.catch(() => {
      // Clear memo so a retry can attempt a fresh load.
      catalogPromises[code] = null;
    });
    catalogPromises[code] = p;
  }
  return catalogPromises[code];
}

/** Max wait for the company default language before showing orgDefault/en (KAN-857). */
const COMPANY_DEFAULT_WAIT_MS = 3000;

// Identity-stable empty messages passed to IntlProvider while catalogs load.
// Using a module-level constant avoids triggering unnecessary re-renders.
const EMPTY_MESSAGES = {};

export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const VALID_LOCALES = SUPPORTED_LOCALES.map((l) => l.code);

/** Legacy key — pre-sign-in screens are always English now; only cleared at sign-out on old devices. */
export const LOCALE_STORAGE_KEY = 'crewvex.locale';

const LocaleContext = createContext(null);

/** Public for Account UI / tests — never trust arbitrary strings from URL or network without this. */
export function resolveLocale(code) {
  if (code == null || code === '') return 'en';
  const normalized = String(code).trim().toLowerCase();
  return VALID_LOCALES.includes(normalized) ? normalized : 'en';
}

function clearStoredLocale() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}


export function LocaleProvider({ children }) {
  const { sessionUser, isSuperAdmin, loading: authLoading, tenantId, tenantLoading } = useAuth();
  const { settings, loadedTenantId, settingsLoadFailed } = useCompanySettings();
  // RD-SEC-051: no write before 2FA — the write-once effect waits for the final assurance.
  const { settled: assuranceSettled, aal: assuranceAal } = useAssuranceSettled(sessionUser?.id ?? null);

  const orgDefault = resolveLocale(settings?.defaultLocale);

  /**
   * Signed-in user's confirmed locale from DB. Deliberately NOT seeded from
   * localStorage — on shared devices this would briefly reveal the previous
   * user's language preference. DB is the only trusted source of truth while
   * signed in; BootGate keeps the splash up until this resolves.
   */
  const [userPrefLocale, setUserPrefLocale] = useState(null);
  const [userPrefFetched, setUserPrefFetched] = useState(false);

  /** User id whose DB preferred_locale came back NULL (write-once candidate). */
  const [nullPrefUserId, setNullPrefUserId] = useState(null);
  const wroteDefaultForRef = useRef(null);

  /** Tracks session transitions so we only clear storage on explicit sign-out, not initial boot. */
  const prevSessionUserIdRef = useRef(null);

  /**
   * Effective locale.
   *   Signed-in: DB pref → org default → 'en' (splash hides the window before DB returns).
   *   Signed-out: ALWAYS 'en' (founder decision) — every pre-sign-in screen is English.
   * The sign-in handoff lock keeps that pre-auth language ('en') while Login is on screen.
   */
  const handoffLocaleLock = useAuthHandoffLocaleLock();
  const locale = useMemo(() => {
    if (sessionUser?.id) {
      if (handoffLocaleLock) return 'en';
      return resolveLocale(userPrefLocale || orgDefault);
    }
    return 'en';
  }, [sessionUser?.id, userPrefLocale, handoffLocaleLock, orgDefault]);

  /**
   * True once the locale we're showing is the FINAL one for the current auth
   * state — never an intermediate guess. BootGate uses this to hold the boot
   * splash until the correct language is confirmed, preventing both the
   * first-visit flash and the post-signin ping-pong.
   */
  // KAN-857: a user with no own language also waits for the company default
  // (bounded) so the first paint is already in the final language.
  const needsCompanyDefault = Boolean(sessionUser?.id) && !isSuperAdmin && userPrefLocale == null;
  const waitKey = sessionUser?.id ? `${sessionUser.id}:${tenantId ?? ''}` : null;
  const [waitExpiredKey, setWaitExpiredKey] = useState(null);
  const waitExpired = waitKey != null && waitExpiredKey === waitKey;
  const companyDefaultSettled = !needsCompanyDefault || (
    tenantLoading ? false
      : !tenantId || loadedTenantId === tenantId || Boolean(settingsLoadFailed)
  );
  // The company-default wait gates the FIRST open only: once passed for this user it
  // latches (cleared at sign-out), so a later settings reload / tenant switch can never
  // un-ready the locale — BootGate would unmount the whole app mid-session.
  const [companyDefaultSeenFor, setCompanyDefaultSeenFor] = useState(null);
  const companyDefaultPassed = companyDefaultSettled || waitExpired
    || (sessionUser?.id != null && companyDefaultSeenFor === sessionUser.id);
  const localeReady = useMemo(() => {
    if (authLoading) return false;
    if (sessionUser?.id) {
      if (!userPrefFetched) return false;
      return companyDefaultPassed;
    }
    return true; // anonymous: always 'en', synchronous
  }, [authLoading, sessionUser?.id, userPrefFetched, companyDefaultPassed]);

  useEffect(() => {
    if (localeReady && sessionUser?.id && companyDefaultSeenFor !== sessionUser.id) {
      setCompanyDefaultSeenFor(sessionUser.id);
    }
  }, [localeReady, sessionUser?.id, companyDefaultSeenFor]);

  useEffect(() => {
    // While Login's handoff lock is up (the 2FA step), company settings do not load
    // before the code, so the clock waits for assurance to settle. With NO lock
    // (a cold reopen on an aal1 session, before Login can mount) the wait runs as
    // before, or BootGate would hold the splash forever.
    if (!sessionUser?.id || !userPrefFetched || !needsCompanyDefault || companyDefaultPassed) return undefined;
    if (!assuranceSettled && handoffLocaleLock) return undefined;
    const t = setTimeout(() => setWaitExpiredKey(waitKey), COMPANY_DEFAULT_WAIT_MS);
    return () => clearTimeout(t);
  }, [sessionUser?.id, userPrefFetched, needsCompanyDefault, companyDefaultPassed, assuranceSettled, handoffLocaleLock, waitKey]);

  /**
   * Loaded catalog map. Null entries = not loaded yet.
   * Both catalogs are lazy chunks; neither is in main.js.
   */
  const [catalogs, setCatalogs] = useState({ en: null, es: null });

  // Retry counter per locale so we attempt at most once before giving up.
  const retryCountRef = useRef({ en: 0, es: 0 });

  /**
   * Attempt to load a catalog. On failure, retry once; on second failure, log
   * a warning and mark the slot with a sentinel so we don't keep retrying.
   */
  const attemptLoad = useCallback((code) => {
    loadCatalog(code)
      .then((data) => {
        setCatalogs((prev) => prev[code] === data ? prev : { ...prev, [code]: data });
      })
      .catch(() => {
        const count = retryCountRef.current[code] || 0;
        if (count < 1) {
          retryCountRef.current = { ...retryCountRef.current, [code]: count + 1 };
          // catalogPromises[code] was cleared in the catch above — retry.
          attemptLoad(code);
        } else {
          // Two failures — unblock with whatever we have; log so it's diagnosable.
          console.warn(`[LocaleContext] Failed to load ${code} catalog after retry.`);
          // Mark as attempted-but-failed with an empty object so messagesReady unblocks.
          setCatalogs((prev) => ({ ...prev, [code]: EMPTY_MESSAGES }));
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // KAN-857: the user's real language, preloaded even while the handoff lock forces 'en'.
  const targetLocale = sessionUser?.id ? resolveLocale(userPrefLocale || orgDefault) : 'en';

  useEffect(() => {
    // Always load the active locale's catalog.
    if (!catalogs[locale]) {
      attemptLoad(locale);
    }
    if (!catalogs[targetLocale]) {
      attemptLoad(targetLocale);
    }
    // Always keep EN loaded: fallback for ES, and the catalog PreAuthEnglish renders from.
    if (!catalogs.en) {
      attemptLoad('en');
    }
  }, [locale, targetLocale, catalogs, attemptLoad]);

  /**
   * The messages to hand to IntlProvider:
   *   1. Active locale's catalog (preferred).
   *   2. EN catalog as fallback (for missing keys or failed ES load).
   *   3. EMPTY_MESSAGES as last resort (BootGate prevents rendering, but
   *      IntlProvider requires a non-null messages prop).
   */
  const messages = useMemo(() => (
    catalogs[locale] || catalogs.en || null
  ), [locale, catalogs]);

  /**
   * True once the ACTIVE locale's catalog has landed (or failed after retry —
   * stored as {}, truthy), latched per session: BootGate unmounts the app while
   * blocking, so a later switch to a not-yet-cached language must not un-ready it.
   * The latch skips the handoff lock (locale forced 'en') so it can't pre-empt 'es'.
   */
  const sessionKey = sessionUser?.id ?? 'anon';
  const activeCatalogReady = Boolean(catalogs[locale]);
  const [bootedKey, setBootedKey] = useState(null);
  useEffect(() => {
    if (localeReady && activeCatalogReady && !handoffLocaleLock && bootedKey !== sessionKey) {
      setBootedKey(sessionKey);
    }
  }, [localeReady, activeCatalogReady, handoffLocaleLock, bootedKey, sessionKey]);
  const messagesReady = activeCatalogReady || bootedKey === sessionKey;

  /**
   * Keep <html lang> in sync once the locale is confirmed. Helps screen readers
   * and Safari spellcheck. Also re-assert the Chrome Translate opt-out
   * (RD-I18N-007): CrewVex ships its own EN/ES catalogs — page-Translate
   * mangles the brand and can crash React during onboarding.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!localeReady) return;
    document.documentElement.lang = locale;
    applyDocumentNoTranslate(document);
  }, [locale, localeReady]);

  /**
   * Session transitions. On sign-in: fetch DB preferred_locale (source of truth).
   * On sign-out: clear cached locale so the next user on a shared device gets
   * their own default (not the previous user's preference).
   */
  useEffect(() => {
    const currentId = sessionUser?.id ?? null;
    const prevId = prevSessionUserIdRef.current;
    prevSessionUserIdRef.current = currentId;

    if (!currentId) {
      setUserPrefLocale(null);
      setUserPrefFetched(false);
      setNullPrefUserId(null);
      setCompanyDefaultSeenFor(null);
      setWaitExpiredKey(null);
      wroteDefaultForRef.current = null;
      if (prevId) {
        // Explicit sign-out: drop the legacy cached locale key on old devices.
        clearStoredLocale();
      }
      return undefined;
    }

    let cancelled = false;
    const table = isSuperAdmin ? 'platform_users' : 'profiles';
    const column = isSuperAdmin ? 'user_id' : 'id';
    supabase
      .from(table)
      .select('preferred_locale')
      .eq(column, currentId)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const raw = data?.preferred_locale;
        setUserPrefLocale(raw ? resolveLocale(raw) : null);
        setUserPrefFetched(true);
        // Write-once: a NULL tenant preference is persisted by the effect below
        // (company default, after settings load). Platform users keep their own column.
        if (!raw && !isSuperAdmin) setNullPrefUserId(currentId);
      })
      .catch(() => {
        // Query failed — unblock the boot gate with a best-effort guess and let
        // the UI continue in orgDefault / 'en' rather than hanging on the splash.
        if (cancelled) return;
        setUserPrefLocale(null);
        setUserPrefFetched(true);
      });
    return () => { cancelled = true; };
  }, [sessionUser?.id, isSuperAdmin]);

  /**
   * Write-once (RD-I18N-006): persist the COMPANY DEFAULT the user actually saw
   * (never the phone language), only after THIS tenant's settings were read
   * successfully — never the normalized 'en' default of a failed/pending read.
   * Fire-and-forget, own row only, skipped for platform users.
   */
  useEffect(() => {
    const currentId = sessionUser?.id ?? null;
    // userPrefLocale set meanwhile (Account → Language) wins — never overwrite it.
    if (!currentId || !assuranceSettled || isSuperAdmin || nullPrefUserId !== currentId || userPrefLocale != null) return;
    if (!tenantId || loadedTenantId !== tenantId || wroteDefaultForRef.current === currentId) return;
    wroteDefaultForRef.current = currentId;
    const inUse = resolveLocale(settings?.defaultLocale);
    // KAN-708: the DB refuses this before 2FA (e.g. a forced-enrollment user at aal1) —
    // a 0-row/failed write re-arms, so it retries once the session's aal changes.
    const rearm = () => { if (wroteDefaultForRef.current === currentId) wroteDefaultForRef.current = null; };
    Promise.resolve(supabase.from('profiles').update({ preferred_locale: inUse }).eq('id', currentId).select('id'))
      .then((res) => { if (res?.error || !res?.data?.length) rearm(); }, rearm);
  }, [sessionUser?.id, assuranceSettled, assuranceAal, isSuperAdmin, nullPrefUserId, userPrefLocale, tenantId, loadedTenantId, settings?.defaultLocale]);

  // Latest values for the async switch + the resume re-read (refs: never stale inside a promise).
  const targetLocaleRef = useRef('en');
  targetLocaleRef.current = targetLocale;
  const localChangeCountRef = useRef(0);
  const sessionUserIdRef = useRef(null);
  sessionUserIdRef.current = sessionUser?.id ?? null;

  /**
   * Switch language with ONE whole-screen cross-fade (KAN-304). The new catalog is LOADED first, then the
   * catalog + preference commit together inside the fade (flushSync), so the old language fades straight into
   * the new one — never into raw ids or the English fallback. `messagesReady` is never touched (it is latched
   * for the session, KAN-857). A failed load falls back to the old instant path (the loader effect retries).
   * Resolves once the fade has finished.
   */
  const applyLocale = useCallback(async (code) => {
    const next = resolveLocale(code);
    localChangeCountRef.current += 1;
    const seq = localChangeCountRef.current;
    const userAtStart = sessionUserIdRef.current;
    if (next === targetLocaleRef.current) { setUserPrefLocale(next); return; }
    let data = null;
    try { data = await loadCatalog(next); } catch { data = null; }
    // A newer switch (or sign-out / another user) arrived while the catalog loaded: the latest wins.
    if (seq !== localChangeCountRef.current || sessionUserIdRef.current !== userAtStart) return;
    const commit = () => {
      if (data) setCatalogs((prev) => (prev[next] === data ? prev : { ...prev, [next]: data }));
      setUserPrefLocale(next);
    };
    if (!data) { commit(); return; }
    await runScreenCrossFade(() => { flushSync(commit); });
  }, []);

  // Signed-out display is always English: setLocale only applies to a signed-in user.
  const setLocale = useCallback((code) => {
    if (!sessionUser?.id) return Promise.resolve();
    return applyLocale(code);
  }, [sessionUser?.id, applyLocale]);

  useLocaleResumeSync({
    userId: sessionUser?.id ?? null,
    isSuperAdmin,
    enabled: userPrefFetched && assuranceSettled,
    targetLocaleRef,
    localChangeCountRef,
    applyLocale,
  });

  const englishMessages = catalogs.en;
  const contextValue = useMemo(
    () => ({ locale, setLocale, localeReady, messagesReady, englishMessages }),
    [locale, setLocale, localeReady, messagesReady, englishMessages],
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider locale={locale} messages={messages || EMPTY_MESSAGES} defaultLocale="en">
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

/** English catalog (null until loaded); safe outside the provider. Used by PreAuthEnglish. */
export function useEnglishMessages() {
  const ctx = useContext(LocaleContext);
  return ctx?.englishMessages || null;
}
